import { api } from "@/app/services/api.service";

export const LEGAL_DOCUMENTS_SYNC_ITEMS_PATH = "legal-documents/sync-items";

export interface LegalDocumentSyncItem {
  id: string | null;
  instrumentNo: string | null;
  trackingNumber: string | null;
  documentType: string | null;
  instrumentNoKey: string | null;
  bnrStatus: string | null;
  instrumentNoResponse: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  raw: Record<string, unknown>;
}

export interface LegalDocumentSyncFilters {
  page: number;
  perPage: number;
  search?: string;
  bnrKey?: string;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface LegalDocumentSyncPage {
  content: LegalDocumentSyncItem[];
  totalElements: number;
  totalPages: number;
}

interface ApiEnvelope<T> {
  error?: string | null;
  message?: string | null;
  data?: T;
}

interface PaginatedSyncResponse {
  content?: unknown[];
  totalPages?: number;
  totalElements?: number;
}

export function extractErrorMessage(error: unknown, fallback: string) {
  const err = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return err.response?.data?.message
    || err.response?.data?.error
    || err.message
    || fallback;
}

function readString(record: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = record[key];
    if (value != null && value !== "") return String(value);
  }
  return null;
}

function unwrapEnvelope<T>(root: unknown): T {
  if (root != null && typeof root === "object" && "data" in root) {
    const envelope = root as ApiEnvelope<T>;
    if (envelope.error) {
      throw new Error(envelope.message || envelope.error);
    }
    if (envelope.data != null) {
      return envelope.data;
    }
  }
  return root as T;
}

function normalizeSyncItem(item: unknown): LegalDocumentSyncItem | null {
  if (item == null || typeof item !== "object") return null;
  const record = item as Record<string, unknown>;

  return {
    id: readString(record, ["id"]),
    instrumentNo: readString(record, ["instrumentNo", "instrumentNumber", "instrument_number"]),
    trackingNumber: readString(record, ["trackingNumber", "tracking_number"]),
    documentType: readString(record, ["documentType", "document_type"]),
    instrumentNoKey: readString(record, ["instrumentNoKey", "instrument_no_key", "bnrKey", "bnr_key"]),
    bnrStatus: readString(record, ["bnrStatus", "bnr_status", "status"]),
    instrumentNoResponse: readString(record, ["instrumentNoResponse", "instrument_no_response"]),
    createdAt: readString(record, ["createdAt", "created_at"]),
    updatedAt: readString(record, ["updatedAt", "updated_at"]),
    raw: record,
  };
}

function extractPageRecords(root: unknown): LegalDocumentSyncPage {
  const pageData = unwrapEnvelope<PaginatedSyncResponse | Record<string, unknown>>(root);

  if (pageData != null && typeof pageData === "object" && Array.isArray((pageData as PaginatedSyncResponse).content)) {
    const paginated = pageData as PaginatedSyncResponse;
    const content = (paginated.content ?? [])
      .map((item) => normalizeSyncItem(item))
      .filter((item): item is LegalDocumentSyncItem => item != null);

    return {
      content,
      totalElements: Number(paginated.totalElements ?? content.length),
      totalPages: Number(paginated.totalPages ?? 0),
    };
  }

  if (Array.isArray(pageData)) {
    const content = pageData
      .map((item) => normalizeSyncItem(item))
      .filter((item): item is LegalDocumentSyncItem => item != null);

    return {
      content,
      totalElements: content.length,
      totalPages: content.length > 0 ? 1 : 0,
    };
  }

  return { content: [], totalElements: 0, totalPages: 0 };
}

export function resolveInstrumentNumber(row: LegalDocumentSyncItem): string | null {
  return row.instrumentNo || readString(row.raw, ["instrumentNo", "instrumentNumber", "instrument_number"]);
}

export function formatEnumLabel(value: string | null) {
  if (!value) return "—";
  return value
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
}

export function resolveSyncItemId(row: LegalDocumentSyncItem): string | null {
  return row.id || readString(row.raw, ["id"]);
}

export function canRetrySyncItem(row: LegalDocumentSyncItem): boolean {
  return resolveSyncItemId(row) != null;
}

export async function fetchLegalDocumentSyncItems(
  filters: LegalDocumentSyncFilters
): Promise<LegalDocumentSyncPage> {
  const params: Record<string, string | number> = {
    page: filters.page,
    per_page: filters.perPage,
  };

  if (filters.search?.trim()) params.search = filters.search.trim();
  if (filters.bnrKey?.trim()) params.bnrKey = filters.bnrKey.trim();
  if (filters.status?.trim()) params.status = filters.status.trim();
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;

  const res = await api().get(LEGAL_DOCUMENTS_SYNC_ITEMS_PATH, { params });
  return extractPageRecords(res.data);
}

export function canReplaceSyncItem(row: LegalDocumentSyncItem): boolean {
  if (!resolveSyncItemId(row)) return false;
  return row.instrumentNoKey === "DOCUMENT_ATTACHMENT";
}

export async function replaceLegalDocumentSyncItem(id: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return api().post(`${LEGAL_DOCUMENTS_SYNC_ITEMS_PATH}/${id}/replace`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function retryLegalDocumentSyncItem(id: string) {
  return api().post(`${LEGAL_DOCUMENTS_SYNC_ITEMS_PATH}/${id}`);
}

export async function retryLegalDocumentAttachments(instrumentNumber: string) {
  return api().post("/legal-documents/attachments/retry", null, {
    params: { instrumentNo: instrumentNumber },
  });
}
